import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LayoutGrid, List, Plus, Search, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Modal } from "@/components/ui/Modal";
import { StatusPill } from "@/components/ui/StatusPill";
import { ProspectForm } from "@/components/prospects/ProspectForm";
import { api } from "@/lib/api";
import { usePeriod } from "@/lib/period";
import {
  cn,
  formatRelativeDate,
  fullName,
  initials,
  isPast,
} from "@/lib/utils";
import { useToast } from "@/components/ui/Toast";
import type { Prospect, ProspectStatus } from "@shared/schema";
import { PROSPECT_STATUS_LABELS, PROSPECT_STATUS_ORDER } from "@shared/labels";
import { PRODUCTS } from "@shared/products";
import { isStale, parisDateOf } from "@shared/followup";

type View = "table" | "kanban";
type Tab = "actifs" | "perdus" | "anciens";

const ARCHIVED_STATUSES = ["closed_lost", "rembourse"] as const;

const isArchivedStatus = (s: ProspectStatus) =>
  (ARCHIVED_STATUSES as readonly string[]).includes(s);

export function ProspectsPage() {
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>(
    (localStorage.getItem("pccrm.prospects.view") as View) ?? "table",
  );
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab =
    searchParams.get("tab") === "anciens"
      ? "anciens"
      : searchParams.get("tab") === "perdus"
        ? "perdus"
        : "actifs";
  const [filterStatus, setFilterStatus] = useState<"" | ProspectStatus>("");
  // Un filtre de statut propre à un onglet ne doit pas survivre au changement d'onglet (y compris
  // par l'URL), sinon la liste paraît vide.
  useEffect(() => setFilterStatus(""), [tab]);
  const [createOpen, setCreateOpen] = useState(false);
  // Prospects mid-deletion: hidden from the UI but only deleted server-side
  // once the 5s undo window elapses.
  const [removingIds, setRemovingIds] = useState<Set<number>>(new Set());
  const deleteTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const toast = useToast();
  const { inPeriod } = usePeriod();

  useEffect(() => {
    localStorage.setItem("pccrm.prospects.view", view);
  }, [view]);

  async function refresh() {
    setLoading(true);
    try {
      const { prospects } = await api.listProspects();
      setProspects(prospects);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  // Deferred delete with a 5s undo window: hide the row immediately, only hit
  // the API once the timer fires. "Annuler" cancels the timer and restores it.
  function unhide(id: number) {
    setRemovingIds((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
  }

  function handleDelete(p: Prospect) {
    setRemovingIds((s) => new Set(s).add(p.id));

    const timer = setTimeout(async () => {
      deleteTimers.current.delete(p.id);
      try {
        await api.deleteProspect(p.id);
        setProspects((ps) => ps.filter((x) => x.id !== p.id));
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Erreur");
      } finally {
        unhide(p.id);
      }
    }, 5000);
    deleteTimers.current.set(p.id, timer);

    toast.notify({
      message: `${fullName(p)} supprimé`,
      duration: 5000,
      action: {
        label: "Annuler",
        onClick: () => {
          const t = deleteTimers.current.get(p.id);
          if (t) clearTimeout(t);
          deleteTimers.current.delete(p.id);
          unhide(p.id);
        },
      },
    });
  }

  // If we leave the page while deletes are pending, commit them right away so
  // the action isn't silently lost.
  useEffect(() => {
    const timers = deleteTimers.current;
    return () => {
      for (const [id, t] of timers) {
        clearTimeout(t);
        api.deleteProspect(id).catch(() => undefined);
      }
      timers.clear();
    };
  }, []);

  // Keyboard shortcut: N to create
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      )
        return;
      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        setCreateOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const visible = useMemo(
    () => prospects.filter((p) => !removingIds.has(p.id)),
    [prospects, removingIds],
  );

  // Les prospects de la période sélectionnée (cohorte par date de création).
  const scoped = useMemo(
    () => visible.filter((p) => inPeriod(p.createdAt)),
    [visible, inPeriod],
  );

  const today = parisDateOf(new Date().toISOString()) ?? "";

  // « Anciens » ignore volontairement le filtre de période : même ensemble que
  // le compteur de la page Aujourd'hui (staleCount).
  const staleList = useMemo(
    () => visible.filter((p) => isStale(p, today)),
    [visible, today],
  );
  const staleIds = useMemo(
    () => new Set(staleList.map((p) => p.id)),
    [staleList],
  );
  const archivedCount = useMemo(
    () => scoped.filter((p) => isArchivedStatus(p.status)).length,
    [scoped],
  );
  const activeList = useMemo(
    () =>
      scoped.filter((p) => !isArchivedStatus(p.status) && !staleIds.has(p.id)),
    [scoped, staleIds],
  );
  const activeCount = activeList.length;
  const staleCount = staleList.length;

  // replace: pas de boucle au retour arrière.
  function switchTab(next: Tab) {
    setSearchParams(next === "actifs" ? {} : { tab: next }, { replace: true });
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base =
      tab === "anciens"
        ? staleList
        : tab === "perdus"
          ? scoped.filter((p) => isArchivedStatus(p.status))
          : activeList;
    return base.filter((p) => {
      if (filterStatus && p.status !== filterStatus) return false;
      if (!q) return true;
      return [p.firstName, p.lastName, p.email, p.phone, p.context]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [scoped, staleList, activeList, search, filterStatus, tab]);

  const statusOptions = useMemo(
    () =>
      PROSPECT_STATUS_ORDER.filter((s) =>
        tab === "perdus" ? isArchivedStatus(s) : !isArchivedStatus(s),
      ),
    [tab],
  );

  const kanbanStatuses = statusOptions;

  return (
    <>
      <PageHeader
        title="Prospects"
        subtitle={`${activeCount} actifs${
          archivedCount > 0 ? ` · ${archivedCount} perdus` : ""
        } · raccourci N pour ajouter`}
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setCreateOpen(true)}
          >
            <Plus size={16} /> Nouveau prospect
          </button>
        }
      />

      <div className="mb-4 inline-flex rounded-[var(--radius-md)] border border-[var(--color-border-strong)] p-0.5 bg-[var(--color-surface)]">
        <button
          type="button"
          onClick={() => switchTab("actifs")}
          className={cn(
            "btn btn-ghost btn-sm",
            tab === "actifs" && "bg-[var(--color-muted)]",
          )}
          aria-pressed={tab === "actifs"}
        >
          Actifs
          <span className="text-xs text-[var(--color-muted-foreground)] tabular">
            {activeCount}
          </span>
        </button>
        <button
          type="button"
          onClick={() => switchTab("perdus")}
          className={cn(
            "btn btn-ghost btn-sm",
            tab === "perdus" && "bg-[var(--color-muted)]",
          )}
          aria-pressed={tab === "perdus"}
        >
          Perdus
          <span className="text-xs text-[var(--color-muted-foreground)] tabular">
            {archivedCount}
          </span>
        </button>
        <button
          type="button"
          onClick={() => switchTab("anciens")}
          className={cn(
            "btn btn-ghost btn-sm",
            tab === "anciens" && "bg-[var(--color-muted)]",
          )}
          aria-pressed={tab === "anciens"}
        >
          Anciens
          <span className="text-xs text-[var(--color-muted-foreground)] tabular">
            {staleCount}
          </span>
        </button>
      </div>
      {tab === "anciens" && (
        <p className="mb-4 text-sm text-[var(--color-muted-foreground)]">
          Sans réponse depuis plus de 7 jours. Pose une nouvelle date de relance
          pour faire revenir quelqu'un. Ignore le filtre de période.
        </p>
      )}

      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 min-w-0">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-muted-foreground)] pointer-events-none"
          />
          <input
            className="input-base pl-9"
            placeholder="Rechercher (nom, email, téléphone, contexte…)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="input-base sm:w-56"
          value={filterStatus}
          onChange={(e) =>
            setFilterStatus(e.target.value as "" | ProspectStatus)
          }
        >
          <option value="">Tous les statuts</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {PROSPECT_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
        <div className="inline-flex rounded-[var(--radius-md)] border border-[var(--color-border-strong)] p-0.5 bg-[var(--color-surface)] self-start sm:self-auto">
          <button
            type="button"
            onClick={() => setView("table")}
            className={cn(
              "btn btn-ghost btn-sm",
              view === "table" && "bg-[var(--color-muted)]",
            )}
            aria-pressed={view === "table"}
          >
            <List size={14} /> Table
          </button>
          <button
            type="button"
            onClick={() => setView("kanban")}
            className={cn(
              "btn btn-ghost btn-sm",
              view === "kanban" && "bg-[var(--color-muted)]",
            )}
            aria-pressed={view === "kanban"}
          >
            <LayoutGrid size={14} /> Kanban
          </button>
        </div>
      </div>

      {loading ? (
        <div className="surface p-8 text-center text-sm text-[var(--color-muted-foreground)]">
          Chargement…
        </div>
      ) : filtered.length === 0 ? (
        <div className="surface p-10 text-center">
          <p className="text-[var(--color-muted-foreground)]">
            Aucun prospect ne correspond.
          </p>
        </div>
      ) : view === "table" ? (
        <TableView prospects={filtered} onDelete={handleDelete} />
      ) : (
        <KanbanView prospects={filtered} statuses={kanbanStatuses} />
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Nouveau prospect"
        description="Ajoute un contact dans le pipeline"
        size="lg"
      >
        <ProspectForm
          onSubmit={async (values) => {
            await api.createProspect(values);
            toast.success("Prospect créé");
            setCreateOpen(false);
            refresh();
          }}
          submitLabel="Créer le prospect"
        />
      </Modal>
    </>
  );
}

function TableView({
  prospects,
  onDelete,
}: {
  prospects: Prospect[];
  onDelete: (p: Prospect) => void;
}) {
  const navigate = useNavigate();
  return (
    <>
    <ul className="md:hidden space-y-2">
      {prospects.map((p) => {
        const overdue =
          !!p.nextFollowUpAt &&
          isPast(p.nextFollowUpAt) &&
          p.status !== "closed_won" &&
          p.status !== "closed_lost";
        return (
          <li key={p.id}>
            <Link
              to={`/prospects/${p.id}`}
              className="surface flex items-center gap-3 p-3 min-h-[44px]"
            >
              <div className="avatar w-9 h-9 shrink-0">
                {initials(fullName(p))}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-[var(--color-navy-700)] truncate">
                    {fullName(p)}
                  </span>
                  <span className="shrink-0">
                    <StatusPill status={p.status} />
                  </span>
                </div>
                {(p.nextFollowUpAt || p.productInterest) && (
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                    {p.nextFollowUpAt && (
                      <span
                        className={cn(
                          "tabular",
                          overdue
                            ? "text-[var(--color-coral-600)] font-medium"
                            : "text-[var(--color-muted-foreground)]",
                        )}
                      >
                        {formatRelativeDate(p.nextFollowUpAt)}
                      </span>
                    )}
                    {p.productInterest && (
                      <span className="text-[var(--color-muted-foreground)]">
                        {PRODUCTS[p.productInterest].label}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
    <div className="surface overflow-hidden hidden md:block">
      <div className="overflow-x-auto">
        <table className="table-base">
          <thead>
            <tr>
              <th>Prospect</th>
              <th>Contact</th>
              <th>Statut</th>
              <th>Produit visé</th>
              <th>Prochaine relance</th>
              <th className="w-px"></th>
            </tr>
          </thead>
          <tbody>
            {prospects.map((p) => (
              <tr
                key={p.id}
                onClick={() => navigate(`/prospects/${p.id}`)}
                className="cursor-pointer"
              >
                <td>
                  <Link
                    to={`/prospects/${p.id}`}
                    className="flex items-center gap-3 min-w-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="avatar w-9 h-9">{initials(fullName(p))}</div>
                    <div className="min-w-0">
                      <div className="font-medium text-[var(--color-navy-700)] hover:text-[var(--color-coral-600)] truncate">
                        {fullName(p)}
                      </div>
                      {p.context && (
                        <div className="text-xs text-[var(--color-muted-foreground)] truncate max-w-xs">
                          {p.context}
                        </div>
                      )}
                    </div>
                  </Link>
                </td>
                <td className="text-sm">
                  {p.email && (
                    <div className="truncate max-w-[200px]">{p.email}</div>
                  )}
                  {p.phone && (
                    <div className="text-[var(--color-muted-foreground)] tabular">
                      {p.phone}
                    </div>
                  )}
                  {!p.email && !p.phone && (
                    <span className="text-[var(--color-muted-foreground)]">
                      —
                    </span>
                  )}
                </td>
                <td>
                  <StatusPill status={p.status} />
                </td>
                <td className="text-sm">
                  {p.productInterest
                    ? PRODUCTS[p.productInterest].label
                    : "—"}
                </td>
                <td>
                  {p.nextFollowUpAt ? (
                    <span
                      className={cn(
                        "text-sm tabular",
                        isPast(p.nextFollowUpAt) &&
                          p.status !== "closed_won" &&
                          p.status !== "closed_lost"
                          ? "text-[var(--color-coral-600)] font-medium"
                          : "text-[var(--color-foreground)]",
                      )}
                    >
                      {formatRelativeDate(p.nextFollowUpAt)}
                    </span>
                  ) : (
                    <span className="text-[var(--color-muted-foreground)]">
                      —
                    </span>
                  )}
                </td>
                <td className="text-right">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(p);
                    }}
                    aria-label={`Supprimer ${fullName(p)}`}
                    title="Supprimer"
                    className="btn btn-ghost btn-icon btn-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-danger)]"
                  >
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </>
  );
}

function KanbanView({
  prospects,
  statuses,
}: {
  prospects: Prospect[];
  statuses: ProspectStatus[];
}) {
  const grouped = useMemo(() => {
    const g: Record<ProspectStatus, Prospect[]> = {
      a_contacter: [],
      en_discussion: [],
      relance: [],
      closed_won: [],
      closed_lost: [],
      rembourse: [],
    };
    for (const p of prospects) g[p.status].push(p);
    return g;
  }, [prospects]);

  return (
    <div className="grid grid-flow-col auto-cols-[minmax(280px,1fr)] gap-3 overflow-x-auto pb-2">
      {statuses.map((status) => (
        <section key={status} className="surface p-3 min-h-64">
          <header className="flex items-center justify-between mb-3 px-1">
            <StatusPill status={status} />
            <span className="text-xs font-medium text-[var(--color-muted-foreground)] tabular">
              {grouped[status].length}
            </span>
          </header>
          <ul className="space-y-2">
            {grouped[status].map((p) => (
              <li key={p.id}>
                <Link
                  to={`/prospects/${p.id}`}
                  className="block bg-[var(--color-surface-2)] hover:bg-white hover:shadow-sm border border-[var(--color-border)] rounded-[var(--radius-md)] p-3 transition-all"
                >
                  <div className="flex items-start gap-2.5">
                    <div className="avatar w-8 h-8 text-xs">
                      {initials(fullName(p))}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-[var(--color-navy-700)] truncate">
                        {fullName(p)}
                      </div>
                      {p.productInterest && (
                        <div className="text-xs text-[var(--color-muted-foreground)] mt-0.5">
                          {PRODUCTS[p.productInterest].label}
                        </div>
                      )}
                      {p.context && (
                        <p className="text-xs text-[var(--color-muted-foreground)] mt-2 line-clamp-3 leading-snug">
                          {p.context}
                        </p>
                      )}
                      {p.nextFollowUpAt && (
                        <div
                          className={cn(
                            "text-xs mt-2 font-medium tabular",
                            isPast(p.nextFollowUpAt) &&
                              status !== "closed_won" &&
                              status !== "closed_lost"
                              ? "text-[var(--color-coral-600)]"
                              : "text-[var(--color-navy-600)]",
                          )}
                        >
                          ↻ {formatRelativeDate(p.nextFollowUpAt)}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
            {grouped[status].length === 0 && (
              <li className="text-xs text-[var(--color-muted-foreground)] text-center py-6">
                —
              </li>
            )}
          </ul>
        </section>
      ))}
    </div>
  );
}
